/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, Injector, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { type Rest, ActionAbstract, FormControl, FormGroup, SelectComponent } from '@dm/core';
import { NfsPermissionE } from 'app/common/constants';
import { isEqual } from 'lodash';
import { type Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { CreateStyleE } from '../../share.enum';
import { ANONYMOUS_ID_DEFAULT } from '../client-nfs-share/client-nfs-share.const';
import { ClientNfsShareService } from '../client-nfs-share/client-nfs-share.service';
import { CreateNfsShareComponent } from '../create-nfs-share/create-nfs-share.component';
import { NfsShareStrService } from '../nfs-share-str.service';
import { UNPLACEHOLDER } from './../../../../../../common/constants/index';
import { type NFSClient, NFSShare } from './../../../../../../common/types/mo.d';
import { NfsShareService } from './../nfs-share.service';


export interface CreateShare {
    all: { share: NFSShare; permissions: NFSClient[] };
    new: { share: NFSShare; permissions: NFSClient[] };
    copyFrom: { share: NFSShare; permissions: NFSClient[]; permissionNumber: number };
}

@Component({
    selector: 'app-copy-from-nfs-share',
    templateUrl: './copy-from-nfs-share.component.html',
    styleUrls: ['./copy-from-nfs-share.component.scss'],
})

export class CopyFromNfsShareComponent extends ActionAbstract implements OnInit, OnChanges {
    @ViewChild('NFSshareSelect') shareSelect: SelectComponent;
    @Input() vstoreId: string;
    @Input() vstoreName: string;
    @Input() entry: FileSystemEntryE;

    /**
     * 文件系统名称
     */
    @Input() fsName: string;

    // 外部传入的vm
    @Input() vm: FormGroup;
    @Input() shareSwitch = true;

    /**
     * 是否支持智能GUI，默认支持
     */
    @Input() supportSmartGui = true;
    // 智能GUI推荐的共享参数
    @Input() smartGuiNfs?: NFSShare;

    i18n = this.str.i18n;
    shareRest: Rest;
    errorMessage: any;

    CreateStyleE = CreateStyleE;
    // 开关状态, 默认true
    switch = true;
    // 创建模式，默认new
    createStyle = CreateStyleE.New;
    // 选中的文件系统，默认null
    selectShareOpt = null;
    /**
     * 是否属于审计日志文件系统
     */
    isAuditLogFs = false;

    // 查询共享权限的rest subscribe，避免快速点击的时候重复下发返回时序问题导致显示错误。
    $getCount: null | Subscription = null;

    /**
     * new模式下的默认权限
     *  默认是有一条所有人的权限的
     *  所有主机 读写模式 同步写入
     *  0 为true 1 为false
     */
    defaultNewPermission: NFSClient = {
        NAME: '*',
        _type: 'host',
        ACCESSVAL: NfsPermissionE.readAndWrite, // 读写
        ACCESSKRB5: NfsPermissionE.none, // 无权限
        ACCESSKRB5I: NfsPermissionE.none, // 无权限
        ACCESSKRB5P: NfsPermissionE.none, // 无权限
        ANONYMOUSID: ANONYMOUS_ID_DEFAULT, // 默认值65534
        ALLSQUASH: '1',
        ROOTSQUASH: '0', // 默认 root_squash
        SECURE: '1',
    };
    /**
     * 待创建的共享数据，new和copyFrom分开，是为了记录数据。切换new和copyfrom的时候，数据不丢失。
     */
    createShare: CreateShare = {
        all: {
            share: {
                SHAREPATH: UNPLACEHOLDER,
                CHARACTERENCODING: 0,
                DESCRIPTION: '',
                ENABLESHOWSNAPSHOT: true,
            },
            /**
             *  new的共享权限列表
             *  默认是有一条所有人的权限的
             *  所有主机 读写模式 同步写入
             *  0 为true 1 为false
             */
            permissions: [this.defaultNewPermission],
        },
        new: {
            share: {
                SHAREPATH: UNPLACEHOLDER,
                CHARACTERENCODING: 0,
                DESCRIPTION: '',
                ENABLESHOWSNAPSHOT: true,
            },
            /**
             *  new的共享权限列表
             *  默认是有一条所有人的权限的
             *  所有主机 读写模式 同步写入
             *  0 为true 1 为false
             */
            permissions: [this.defaultNewPermission],
        },
        copyFrom: {
            share: {
                SHAREPATH: UNPLACEHOLDER,
                CHARACTERENCODING: 0,
                DESCRIPTION: '',
                ENABLESHOWSNAPSHOT: true,
            },
            permissions: null,
            permissionNumber: 0,
        },
    };
    /**
     * new模式下，权限是否没有变动。
     */
    permissionIsDefault = true;

    constructor(
        injector: Injector,
        public str: NfsShareStrService,
        public service: NfsShareService,
        public clientNfsShareService: ClientNfsShareService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (this.smartGuiNfs) {
            // 更新notify
            this.createShare.new.share.ENABLESHOWSNAPSHOT = this.smartGuiNfs.ENABLESHOWSNAPSHOT;
        }
        this.switch = this.shareSwitch;
        // 如果被赋值关闭，要手动触发一下changhe事件，避免没有disable form
        if (!this.switch) {
            // 在下一个流程中去禁用，暂无更好方法 待优化
            setTimeout(() => {
                this.onSwitchChange(false);
            });
        }
        this.updateDefaultAccessVal();
        this.initVm();
    }

    /**
     * 更新权限默认值
     */
    updateDefaultAccessVal(): void {
        if (this.entry === FileSystemEntryE.AuditLog) {
            this.isAuditLogFs = true;
            // 审计日志文件系统和内部文件系统共享权限只能为0只读
            this.defaultNewPermission.ACCESSVAL = NfsPermissionE.onlyRead;
            return;
        }

        this.defaultNewPermission.ACCESSVAL = NfsPermissionE.readAndWrite;
    }

    ngOnChanges(changes: SimpleChanges): void {
        // 只有当vstore有值的时候才更新rest
        if (changes.vstoreId && this.vstoreId) {
            this.shareRest = this.service.getBatchRest(this.vstoreId);
            this.createShare.copyFrom.permissions = null;
            this.createShare.copyFrom.permissionNumber = 0;
        }
        // fsName可能为空，为空也要更新
        if (changes.fsName) {
            this.changeSharePath(this.fsName);
        }

        this.updateDefaultAccessVal();
    }

    initVm(): void {
        this.vm.addControl('NFScreateStyle', new FormControl(this.createStyle, []));
        this.vm.addControl('NFScopyFromSelect', new FormControl(null, [Validators.required]));
        this.vm.addControl('NFScreateShare', new FormControl(this.createShare, []));

        this.errorMessage = {
            NFScopyFromSelect: {
                required: this.i18n.error.copyFromRequired,
            },
        };

        if (this.createStyle === CreateStyleE.New) {
            this.vm.get('NFScopyFromSelect').disable();
        }
    }

    // 共享开关被切换的时候，需要disable一下
    onSwitchChange($event): void {
        if ($event) {
            this.vm.get('NFScreateShare').enable();
            this.vm.get('NFScreateStyle').enable();
            if (this.createStyle === CreateStyleE.CopyFrom) {
                this.vm.get('NFScopyFromSelect').enable();
            }
            // 下一个周期去渲染下拉
            setTimeout(() => {
                this.shareSelect?.setFixedOption(this.selectShareOpt);
            });
        } else {
            this.vm.get('NFScreateShare').disable();
            this.vm.get('NFScreateStyle').disable();
            if (this.createStyle === CreateStyleE.CopyFrom) {
                this.vm.get('NFScopyFromSelect').disable();
            }
        }
    }

    // copyfrom 的共享被切换的时候
    onCopyShareChange($event): void {
        // 先更新所选中的文件系统
        this.selectShareOpt = $event.option;
        // 无选中项时
        if (!this.selectShareOpt) {
            // 去掉切换时的红色提士 如果不清，每次切换租户都变红，很丑
            // 用异步规避时序问题导致红色错误清除不掉
            Promise.resolve().then(() => {
                (this.vm.get('NFScopyFromSelect') as FormControl).clearValidate();
            });
        } else {
            // 有选中项时
            // 1、查询并更新这个共享下的client数量
            this.updatePermissions(this.selectShareOpt.data);

            // 2、更新copy的share的描述
            this.createShare.copyFrom.share.DESCRIPTION = this.selectShareOpt.data.DESCRIPTION;

            // 3、更新copy的share的ID和vstoreId，是为了查询copyFrom的数据
            this.createShare.copyFrom.share.ID = this.selectShareOpt.data.ID;
            this.createShare.copyFrom.share.vstoreId = this.selectShareOpt.data.vstoreId;

            // 4、初始化createShare下的copyfrom中的permissions数据
            this.createShare.copyFrom.permissions = null;

            // 5、更新copy的share的字符编码
            this.createShare.copyFrom.share.CHARACTERENCODING = this.selectShareOpt.data.CHARACTERENCODING;
            this.createShare.copyFrom.share.ENABLESHOWSNAPSHOT = this.selectShareOpt.data.ENABLESHOWSNAPSHOT;
        }
    }

    // 获取共享权限数量，以及全量的client列表
    updatePermissions(nfs: NFSShare): void {
        if (this.$getCount) {
            this.$getCount.unsubscribe();
            this.$getCount = null;
        }
        this.$getCount = this.clientNfsShareService.getCount(nfs.ID, nfs.vstoreId).pipe(finalize(() => {
            this.$getCount = null;
        })).subscribe(({ data }) => {
            this.createShare.copyFrom.permissionNumber = data.COUNT;
        });
    }

    // 创建模式radio被切换时
    onCreateStyleChange(value: CreateStyleE): void {
        this.createStyle = value;
        const copyFromSelectCtl = this.vm.get('NFScopyFromSelect');
        if (value === CreateStyleE.All) {
            copyFromSelectCtl.disable();
            this.permissionIsDefault = true;
        } else if (value === CreateStyleE.New) {
            copyFromSelectCtl.disable();
        } else if (value === CreateStyleE.CopyFrom) {
            copyFromSelectCtl.enable();
            // 去掉切换时的红色提
            (copyFromSelectCtl as FormControl).clearValidate();
        }
    }

    // 获取共享权限数量的资料
    getPermissionsNumStr(): string {
        const style = this.vm.value.NFScreateStyle;

        if (style === CreateStyleE.All || style === CreateStyleE.New) {
            // 当是new 且 权限还是默认值的时候，返回需要带eveyone
            if (this.permissionIsDefault) {
                return this.i18n.tip.permissionsWithAllClient(
                    this.createShare[style].permissions ?
                        this.createShare[style].permissions.length :
                        this.createShare[style].permissionNumber,
                    this.isAuditLogFs ?
                        this.i18n.tip.allClientOnlyRead :
                        this.i18n.tip.allClient,
                );
            } else {
                return this.i18n.tip.permisssions(
                    this.createShare[style].permissions ?
                        this.createShare[style].permissions.length : this.createShare[style].permissionNumber,
                );
            }
        } else if (style === CreateStyleE.CopyFrom) {
            return this.i18n.tip.permisssions(
                this.createShare[style].permissions ?
                    this.createShare[style].permissions.length : this.createShare[style].permissionNumber,
            );
        }
        return '';
    }

    // 文件系统名改变的时候，自动组装path
    changeSharePath(fsName: string): void {
        const path = fsName ? '/' + fsName + '/' : UNPLACEHOLDER;
        this.createShare.all.share.SHAREPATH = path;
        this.createShare.new.share.SHAREPATH = path;
        this.createShare.copyFrom.share.SHAREPATH = path;
    }

    modifyShare(): void {
        const style = this.vm.value.NFScreateStyle;
        // 复用的 nfs的创建页面，并且要传入新的title

        this.doAction(CreateNfsShareComponent, {
            copyFromStyle: style,
            copyFromShare: this.createShare[style].share,
            copyFromPermissions: this.createShare[style].permissions,
            title: this.i18n.title.modifyShare,
            vstoreName: this.vstoreName,
            supportSmartGui: this.supportSmartGui,
            entry: this.entry,
            vstoreId: this.vstoreId,
            // 传入 -- 是为了展示用
            fileSystem: { NAME: this.fsName ? this.fsName : UNPLACEHOLDER },
            onClose: ({ data }: { data: { share: NFSShare; permissions: NFSClient[] } }) => {
                // 如果没有dada，说明没有改变值
                if (!data) {
                    return;
                }
                this.createShare[style].share = data.share;
                this.createShare[style].permissions = data.permissions;

                // data.permissions在CreateCifsShareComponent中深度拷贝过一次，所以这里判断不用考虑引用问题
                // 当data.permissions和默认的defaultNewPermission不相等或者数量不是1的时候，说明被更改过了，就不展示everyone
                if (style === CreateStyleE.New) {
                    if (data.permissions.length === 1 && isEqual(this.defaultNewPermission, data.permissions[0])) {
                        this.permissionIsDefault = true;
                    } else {
                        this.permissionIsDefault = false;
                    }
                }
            },
        });
    }
}
